/**
 * Leasing helpers — shared formatters + lifecycle classifications.
 *
 * `smlouvaLifecycle` mirrors the legacy row tinting:
 * early termination → red, expired by `datum_do` → grey, otherwise active.
 */

import type { LeasingSmlouva, LeasingSearchResult } from './api';

import { parseFloatLoose } from '@/lib/format';

export type LeasingLifecycle = 'active' | 'expired' | 'terminated';

const SENTINEL_DATE = new Date('2000-01-01T00:00:00Z').getTime();

function isMeaningfulDate(s: string | null | undefined): boolean {
  if (!s) return false;
  const t = Date.parse(s);
  if (Number.isNaN(t)) return false;
  return t > SENTINEL_DATE;
}

export function smlouvaLifecycle(
  s: Pick<LeasingSmlouva, 'predcasne_ukonceno' | 'datum_do'>,
): LeasingLifecycle {
  if (isMeaningfulDate(s.predcasne_ukonceno)) return 'terminated';
  if (s.datum_do) {
    const t = Date.parse(s.datum_do);
    if (!Number.isNaN(t) && t < Date.now()) return 'expired';
  }
  return 'active';
}

export function searchResultLifecycle(
  s: Pick<LeasingSearchResult, 'predcasne_ukonceno' | 'datum_do'>,
): LeasingLifecycle {
  return smlouvaLifecycle(s);
}

const LIFECYCLE_LABEL: Record<LeasingLifecycle, string> = {
  active: 'Aktivní',
  expired: 'Po termínu',
  terminated: 'Předčasně ukončeno',
};

export function lifecycleLabel(l: LeasingLifecycle): string {
  return LIFECYCLE_LABEL[l];
}

export function lifecycleTone(l: LeasingLifecycle): 'ok' | 'warn' | 'neutral' {
  if (l === 'terminated') return 'warn';
  if (l === 'expired') return 'neutral';
  return 'ok';
}

/**
 * `id_smlouvy` is reused across nabidky and smlouvy — same DB column. The
 * legacy uses raw integers, so we render them as a 6-zero-padded mono code
 * for visual rhythm in the table column.
 */
export function smlouvaCode(id: number): string {
  return String(id).padStart(6, '0');
}

export function clientLine(s: { jmeno: string | null }): string {
  return s.jmeno?.trim() || '—';
}

export function searchClientLine(s: { jmeno_zakaznika: string | null }): string {
  return s.jmeno_zakaznika?.trim() || '—';
}

const CZ_DATE_RX = /^(\d{4})-(\d{2})-(\d{2})/;

export function shortDate(s: string | null): string {
  if (!s) return '';
  const m = s.match(CZ_DATE_RX);
  if (!m) return s;
  return `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}`;
}

/**
 * Day-of-month suffix used by the Přehledy reports' "Datum fakturace" /
 * "Datum platby" columns — legacy renders `new Date(datum).getDate() + '.'`
 * (e.g. the 14th → "14."). Returns the empty string for null/invalid input.
 */
export function dayOfMonth(s: string | null | undefined): string {
  if (!s) return '';
  const m = s.match(CZ_DATE_RX);
  if (!m) return '';
  return `${parseInt(m[3], 10)}.`;
}

/** Current month as `YYYY-MM`, the default scope for the Přehledy reports. */
export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ==================== Loose record readers ====================

/**
 * Read a string from a `Record<string, unknown>` field. Returns the empty
 * string for missing/non-string values so callers can `|| '—'` on a single
 * line. Numbers are coerced to strings (the API returns mysql `varchar`
 * fields as strings but `int` as numbers).
 */
export function readStr(rec: Record<string, unknown> | null | undefined, key: string): string {
  if (!rec) return '';
  const v = rec[key];
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}

export function readNum(rec: Record<string, unknown> | null | undefined, key: string): number {
  if (!rec) return 0;
  const v = rec[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * PHP-`floatval` lookalike — parses the leading number from free-text values.
 * The legacy `operak_*_parametry` columns hold strings like `'1 %'` (the
 * `tolerance` default of every record) or `'1.50'`; plain `Number()` (and
 * `readNum`) turn those into 0, which printed as "0 %" on documents.
 */
export function readFloat(rec: Record<string, unknown> | null | undefined, key: string): number {
  if (!rec) return 0;
  const v = rec[key];
  if (typeof v === 'number' || typeof v === 'string') return parseFloatLoose(v);
  return 0;
}
